import React, { useContext, useEffect, useRef, useState } from "react";
import { Box, Flex, Spinner } from "theme-ui";
import { themevals } from "theme/themevals";
import { addProjectFile, updateProject, updateProjectFile } from "api/Projects";
import BasicButton from "components/forms/BasicButton";
import { getPresignedUrl } from "api/Media";
import { trackActivity } from "api/ActivityTracker";
import UserContext from "context/User";
import { useToast } from "components/toast";
import NeededByField from "components/NeededByField";

/**
 * "ADD YOUR PO AND SITE MAPS" WHERE THE CLIENT ALREADY IS (2026-10-12, Jack).
 *
 * Files belong on the project, and the project only exists once a quote is
 * accepted, so clients rarely found the Files tab. This drop zone puts the
 * upload in front of them:
 *   - variant "accepted": inside the quote-accepted window.
 *   - variant "embedded": the "Add your PO or site maps" step of the
 *     project's Next steps card (replaced the full-width nudge, 2026-10-12).
 * Same storage and registration as the project's Files tab.
 *
 * Each uploaded file is labeled in place (pass 3, Jack): what it is (PO /
 * site map / photo / other) and one line of context. For a PO that line is
 * the PO number - it sits next to the PO document and becomes the
 * project's PO number (backend syncs clientPO). "Only have a PO number?"
 * stays for clients with a number and no document.
 */
type Props = {
  projectId?: string | null;
  /**
   * "accepted": the quote-accepted window (own header + date field).
   * "embedded": inside the project's Next steps card (no header/border).
   */
  variant: "accepted" | "embedded";
  /** The project's current PO number. */
  clientPO?: string | null;
  /** project.clientNeededBy (YYYY-MM-DD) and the date given on the RFQ. */
  neededBy?: string | null;
  rfqDate?: string | Date | null;
  onUploaded?: () => void;
};

type Kind = "po" | "site_map" | "photo" | "other";
type Added = { id: string; name: string; kind: Kind; description: string; savedDescription: string; saving: boolean };

const KINDS: { value: Kind; label: string }[] = [
  { value: "po", label: "Purchase order" },
  { value: "site_map", label: "Site map" },
  { value: "photo", label: "Site photo" },
  { value: "other", label: "Other" },
];

// First guess from the file itself; the client corrects it with one click.
const guessKind = (file: File): Kind => {
  const n = file.name.toLowerCase();
  if (/(^|[^a-z])(po|p\.o\.?)([^a-z]|$)|purchase/.test(n)) return "po";
  if (/map|site|plan|layout|zone/.test(n)) return "site_map";
  if (file.type.startsWith("image/")) return "photo";
  return "other";
};

const inputStyle: React.CSSProperties = {
  fontFamily: "Poppins, sans-serif", fontSize: "13px", padding: "7px 10px", border: "1px solid " + themevals.colors.inputOutline,
  borderRadius: "6px", minWidth: "160px", flex: "1 1 200px",
};

const ProjectFileDrop: React.FC<Props> = ({ projectId, variant, clientPO, neededBy, rfqDate, onUploaded }) => {
  const showDate = variant === "accepted";
  const embedded = variant === "embedded";
  const { user } = useContext<any>(UserContext);
  const toast = useToast();
  const inputRef = useRef<HTMLInputElement>(null);
  const [added, setAdded] = useState<Added[]>([]);
  const [busy, setBusy] = useState(false);
  const [drag, setDrag] = useState(false);
  const [poInput, setPoInput] = useState("");
  const [poSaved, setPoSaved] = useState<string | null>(clientPO || null);
  const [poBusy, setPoBusy] = useState(false);
  useEffect(() => setPoSaved(clientPO || null), [clientPO]);

  const patch = (id: string, change: Partial<Added>) => setAdded((list) => list.map((a) => (a.id === id ? { ...a, ...change } : a)));

  const saveLabel = async (row: Added, kind: Kind, description: string) => {
    if (!projectId) return;
    patch(row.id, { kind, description, saving: true });
    const res: any = await updateProjectFile({ id: projectId, fileId: row.id, data: { kind, description } });
    if (res?.id) {
      patch(row.id, { saving: false, savedDescription: description.trim() });
      if (kind === "po" && description.trim()) setPoSaved(description.trim().slice(0, 100));
    } else {
      patch(row.id, { saving: false });
      toast.add("Could not save that. Try again.", "var(--theme-ui-colors-danger)");
    }
  };

  const savePO = async () => {
    const value = poInput.trim();
    if (!value || !projectId) return;
    setPoBusy(true);
    const res: any = await updateProject({ data: { id: projectId }, update: { clientPO: value } });
    setPoBusy(false);
    if (res?.id) {
      setPoSaved(res?.clientPO || value);
      setPoInput("");
    } else {
      toast.add("Could not save the PO number. Try again.", "var(--theme-ui-colors-danger)");
    }
  };

  const upload = async (files: FileList | null) => {
    if (!files?.length || !projectId) return;
    setBusy(true);
    for (const file of Array.from(files)) {
      try {
        const kind = guessKind(file);
        const signed = await getPresignedUrl({ data: { directory: "projects/" + projectId + "/files", name: file.name, type: file.type, size: file.size } });
        if (!signed?.success || !signed?.url) throw new Error("url");
        const put = await fetch(signed.url, { method: "PUT", headers: { "Content-Type": file.type }, body: file });
        if (!put.ok) throw new Error("put");
        const reg = await addProjectFile({ id: projectId, data: { url: signed.location, name: file.name, kind } });
        if (!reg?.id) throw new Error("register");
        setAdded((a) => [...a, { id: reg.id, name: file.name, kind, description: "", savedDescription: "", saving: false }]);
        void trackActivity({
          module: "projects", action: "upload_project_file", actionType: "create", userId: user?.id || null,
          companyId: user?.company?.id || null, entityType: "project", entityId: projectId,
          metadata: { fileId: reg.id, fileName: file.name, kind, source: variant === "accepted" ? "quote_accepted_prompt" : "project_next_steps" },
          userAgent: typeof navigator !== "undefined" ? navigator.userAgent : null,
        });
      } catch {
        toast.add(`Could not upload ${file.name}. Try again or use the project's Files tab.`, "var(--theme-ui-colors-danger)");
      }
    }
    setBusy(false);
    onUploaded?.();
  };

  if (!projectId || user?.accessLevel === "isProvider") return null;

  const hasPOFile = added.some((a) => a.kind === "po");

  return (
    <Box
      sx={{
        border: embedded && !drag ? "none" : "1px dashed " + (drag ? "#000" : themevals.colors.inputOutline),
        backgroundColor: drag ? "#F7F7F5" : embedded ? "transparent" : "#FAFAF8",
        borderRadius: "8px",
        padding: embedded ? "4px 0 0 0" : "14px 16px",
        marginTop: embedded ? "0px" : "16px",
        fontFamily: "Poppins, sans-serif",
      }}
      onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
      onDragLeave={(e) => { e.preventDefault(); setDrag(false); }}
      onDrop={(e) => { e.preventDefault(); setDrag(false); void upload(e.dataTransfer.files); }}
    >
      <Flex sx={{ alignItems: "center", justifyContent: "space-between", gap: "12px", flexWrap: "wrap" }}>
        <Box sx={{ minWidth: 0, flex: "1 1 260px" }}>
          {!embedded && (
            <Box sx={{ fontSize: "14px", fontWeight: 600, color: "#111" }}>Add your PO, site maps or site photos</Box>
          )}
          <Box sx={{ fontSize: "12px", color: themevals.colors.dkgrey, lineHeight: 1.5 }}>
            {embedded
              ? "Drop files here or choose them. Label each one so our team knows what it is."
              : "Your PO lets us bill it right, site maps show where to install, and current photos confirm the site is ready. Drop files here or choose them."}
          </Box>
        </Box>
        <BasicButton type="button" scheme="secondary" outline={true} disabled={busy} $submitting={busy} keepText={true} $loadingColor="#000" onClick={() => inputRef.current?.click()}>
          {added.length ? "Add more" : "Choose files"}
        </BasicButton>
        <input ref={inputRef} type="file" multiple style={{ display: "none" }} onChange={(e) => { void upload(e.target.files); e.target.value = ""; }} />
      </Flex>

      {/* One row per uploaded file: what it is + one line of context. */}
      {added.map((row) => {
        const isPO = row.kind === "po";
        const dirty = row.description.trim() !== row.savedDescription;
        return (
          <Box key={row.id} sx={{ marginTop: "12px", paddingTop: "12px", borderTop: "1px solid " + themevals.colors.inputOutline }}>
            <Flex sx={{ alignItems: "center", gap: "8px", flexWrap: "wrap", marginBottom: "8px" }}>
              <Flex sx={{ alignItems: "center", gap: "8px", minWidth: 0, flex: "1 1 200px" }}>
                {/* Upload state on every file, so nobody wonders if it went through. */}
                <Box as="span" aria-label="Uploaded" sx={{ display: "inline-flex", alignItems: "center", justifyContent: "center", width: "18px", height: "18px", borderRadius: "50%", backgroundColor: "#E6F2DA", color: "#3B6D11", fontSize: "11px", fontWeight: 700, flexShrink: 0 }}>
                  ✓
                </Box>
                <Box sx={{ fontSize: "13px", fontWeight: 600, color: "#111", minWidth: 0, overflowWrap: "anywhere" }}>{row.name}</Box>
                <Box sx={{ fontSize: "12px", color: "#3B6D11", whiteSpace: "nowrap" }}>Uploaded</Box>
              </Flex>
              <Flex sx={{ gap: "6px", flexWrap: "wrap" }}>
                {KINDS.map((k) => (
                  <button
                    key={k.value}
                    type="button"
                    onClick={() => void saveLabel(row, k.value, row.description)}
                    style={{
                      fontFamily: "Poppins, sans-serif", fontSize: "12px", padding: "4px 10px", borderRadius: "999px", cursor: "pointer",
                      border: "1px solid " + (row.kind === k.value ? "#000" : themevals.colors.inputOutline),
                      backgroundColor: row.kind === k.value ? "#000" : "#fff", color: row.kind === k.value ? "#fff" : "#111",
                    }}
                  >
                    {k.label}
                  </button>
                ))}
              </Flex>
            </Flex>
            <Flex sx={{ alignItems: "center", gap: "8px", flexWrap: "wrap" }}>
              <input
                type="text"
                value={row.description}
                maxLength={isPO ? 100 : 500}
                placeholder={isPO ? "PO number" : row.kind === "site_map" ? "What it shows (optional), e.g. areas to mulch" : row.kind === "photo" ? "What it shows (optional), e.g. old mulch removed" : "Description (optional)"}
                onChange={(e) => patch(row.id, { description: e.target.value })}
                onBlur={() => { if (dirty) void saveLabel(row, row.kind, row.description); }}
                onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); if (dirty) void saveLabel(row, row.kind, row.description); } }}
                style={inputStyle}
              />
              <Box sx={{ fontSize: "12px", color: "#27500A", minWidth: "48px" }}>
                {row.saving ? <Spinner size={14} /> : row.savedDescription && !dirty ? "Saved" : dirty ? "Saves when you click away" : ""}
              </Box>
            </Flex>
          </Box>
        );
      })}
      {added.length > 0 && (
        <Flex sx={{ marginTop: "12px", padding: "8px 12px", borderRadius: "6px", backgroundColor: "#F3F8EC", gap: "8px", alignItems: "center", fontSize: "12px", color: "#27500A" }}>
          <strong>{`${added.length} file${added.length === 1 ? "" : "s"} sent to our team.`}</strong>
          <span>Nothing else to do. Labels and notes save automatically; you can add more any time.</span>
        </Flex>
      )}

      {showDate && (
        <Box sx={{ marginTop: "12px", paddingTop: "12px", borderTop: "1px solid " + themevals.colors.inputOutline }}>
          <NeededByField projectId={projectId} neededBy={neededBy} rfqDate={rfqDate} label="Date needed by" />
        </Box>
      )}

      {/* PO number without a document. Hidden once a PO file is labeled. */}
      {(poSaved || !hasPOFile) && (
        <Flex sx={{ alignItems: "center", gap: "8px", marginTop: "12px", paddingTop: "12px", borderTop: "1px solid " + themevals.colors.inputOutline, flexWrap: "wrap", fontSize: "13px" }}>
          {poSaved ? (
            <Box sx={{ color: "#27500A" }}>
              {"PO number on this project: "}<strong>{poSaved}</strong>
            </Box>
          ) : (
            <>
              <Box sx={{ color: "#111", fontWeight: 500 }}>Only have a PO number?</Box>
              <input
                type="text"
                value={poInput}
                maxLength={100}
                placeholder="PO number"
                onChange={(e) => setPoInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); void savePO(); } }}
                style={{ ...inputStyle, maxWidth: "240px", flex: "1 1 160px" }}
              />
              <BasicButton type="button" scheme="secondary" outline={true} disabled={!poInput.trim() || poBusy} $submitting={poBusy} keepText={true} $loadingColor="#000" onClick={() => void savePO()}>
                Save
              </BasicButton>
            </>
          )}
        </Flex>
      )}
    </Box>
  );
};

export default ProjectFileDrop;
